import React, { FC, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import Checkbox from "shared/Checkbox/Checkbox";
import ButtonThird from "shared/Button/ButtonThird";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonClose from "shared/ButtonClose/ButtonClose";
import { RoomType } from "redux/reducers/Properties/PropertiesSliceTypes";
import { getIconComponent } from "shared/Icon/IconDymamiclisting";

interface MobileFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  amenities: string[];
  features: string[];
  propertyCategory: string[];
  roomTypeId: string;
  AmenitiesCategories: any[];
  featuresCategories: any[];
  propertiesCategories: any[];
  roomTypes: RoomType[];
  onAmenitiesChange: (selectedAmenities: string[]) => void;
  onFeaturesChange: (selectedFeatures: string[]) => void;
  onPropertyCategoryChange: (selectedCategories: string[]) => void;
  onRoomTypeChange: (roomTypeId: string) => void;
  onClear: () => void;
  onApply: (close: () => void) => void;
  renderXClear: () => JSX.Element;
}

const MobileFiltersModal: FC<MobileFiltersModalProps> = ({
  isOpen,
  onClose,
  onOpen,
  amenities,
  features,
  propertyCategory,
  roomTypeId,
  AmenitiesCategories,
  featuresCategories,
  propertiesCategories,
  roomTypes,
  onAmenitiesChange,
  onFeaturesChange,
  onPropertyCategoryChange,
  onRoomTypeChange,
  onClear,
  onApply,
  renderXClear,
}) => {
  const appliedFiltersCount =
    (amenities?.length ? 1 : 0) +
    (propertyCategory?.length ? 1 : 0) +
    (roomTypeId?.length ? 1 : 0) +
    (features?.length ? 1 : 0);

  return (
    <div>
      {/* ✅ Trigger button - opens modal */}
      <div
        className={`flex items-center justify-center px-4 py-2 text-sm rounded-full border cursor-pointer focus:outline-none transition-all ${
          appliedFiltersCount > 0
            ? "bg-[#f5694b] text-white border-[#f5694b]"
            : "border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:border-neutral-400 dark:hover:border-neutral-500 bg-white dark:bg-neutral-800"
        }`}
        onClick={onOpen}
      >
        <span>
          <span className="hidden sm:inline">More</span> filters
          {appliedFiltersCount > 0 && ` (${appliedFiltersCount})`}
        </span>
        {appliedFiltersCount > 0 && renderXClear()}
      </div>

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={onClose}>
          <div className="min-h-screen text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-40 dark:bg-opacity-60" />
            </Transition.Child>
            <span className="inline-block h-screen align-middle" aria-hidden="true">
              &#8203;
            </span>

            <Transition.Child
              className="inline-block py-8 px-2 h-screen w-full max-w-4xl"
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-flex flex-col w-full max-w-4xl text-left align-middle transition-all transform overflow-hidden rounded-2xl bg-white dark:bg-neutral-900 dark:border dark:border-neutral-700 shadow-xl h-full">
                <div className="relative flex-shrink-0 px-6 py-4 border-b border-neutral-200 dark:border-neutral-800 text-center">
                  <Dialog.Title className="text-lg font-medium leading-6 text-neutral-900 dark:text-neutral-100">
                    More filters
                  </Dialog.Title>
                  <span className="absolute left-3 top-3">
                    <ButtonClose onClick={onClose} />
                  </span>
                </div>

                <div className="flex-grow overflow-y-auto">
                  <div className="px-4 sm:px-6 divide-y divide-neutral-200 dark:divide-neutral-800">
                    {/* Amenities Section */}
                    <div className="py-7">
                      <h3 className="text-xl font-medium text-neutral-900 dark:text-neutral-100">
                        Amenities
                      </h3>

                      <div className="relative grid grid-cols-2 gap-4 p-4">
                        {AmenitiesCategories?.map((amenity: any) => (
                          <div key={amenity.id} className="flex items-center space-x-2">
                            <Checkbox
                              name="amenities"
                              isChecked={amenities?.includes(String(amenity.id))}
                              onChange={(e) => {
                                const selectedAmenities = e
                                  ? [...amenities, String(amenity.id)]
                                  : amenities.filter((a: string) => a !== String(amenity.id));
                                onAmenitiesChange(selectedAmenities);
                              }}
                            />
                            <label
                              htmlFor={`amenity-${amenity.id}`}
                              className="flex items-center text-neutral-700 dark:text-neutral-300"
                            >
                              {getIconComponent(amenity.icon_name)?.({
                                className: "text-2xl mr-2",
                              })}
                              {amenity.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Room types Section */}
                    <div className="py-7">
                      <h3 className="text-xl font-medium text-neutral-900 dark:text-neutral-100">
                        Room types
                      </h3>
                      <div className="relative grid grid-cols-2 gap-4 p-4">
                        {roomTypes?.map((room: RoomType) => (
                          <div key={room.id} className="flex items-center space-x-2">
                            <Checkbox
                              name="room_type_id"
                              isChecked={roomTypeId === String(room.id)}
                              onChange={(e) => {
                                const selectedRoomType = e ? String(room.id) : "";
                                onRoomTypeChange(selectedRoomType);
                              }}
                            />
                            <label
                              htmlFor={`room_type_id-${room.id}`}
                              className="text-neutral-700 dark:text-neutral-300"
                            >
                              {room.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Property Category Section */}
                    <div className="py-7">
                      <h3 className="text-xl font-medium text-neutral-900 dark:text-neutral-100">
                        Property Category
                      </h3>
                      <div className="mt-6 relative">
                        <div className="relative grid grid-cols-2 gap-4 p-4">
                          {propertiesCategories?.map((item: any) => (
                            <div key={item.id} className="flex items-center space-x-2">
                              <Checkbox
                                name="property_category"
                                isChecked={propertyCategory?.includes(String(item.id))}
                                onChange={(e) => {
                                  const selectedTypes = e
                                    ? [
                                        ...(Array.isArray(propertyCategory)
                                          ? propertyCategory
                                          : []),
                                        String(item.id),
                                      ]
                                    : Array.isArray(propertyCategory)
                                    ? propertyCategory.filter(
                                        (t: string) => t !== String(item.id)
                                      )
                                    : [];
                                  onPropertyCategoryChange(selectedTypes);
                                }}
                              />
                              <label
                                htmlFor={`type-${item.id}`}
                                className="text-neutral-700 dark:text-neutral-300"
                              >
                                {item.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Property Feature Section */}
                    <div className="py-7">
                      <h3 className="text-xl font-medium text-neutral-900 dark:text-neutral-100">
                        Property feature
                      </h3>
                      <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-4 p-4">
                        {featuresCategories?.map((item: any) => {
                          const idStr = String(item.id);
                          const isChecked = features?.includes(idStr);

                          return (
                            <div key={item.id} className="flex items-center space-x-2">
                              <Checkbox
                                name="features"
                                isChecked={isChecked}
                                onChange={() => {
                                  let updatedFeatures;
                                  if (isChecked) {
                                    updatedFeatures = features.filter(
                                      (f: string) => f !== idStr
                                    );
                                  } else {
                                    updatedFeatures = [...features, idStr];
                                  }
                                  onFeaturesChange(updatedFeatures);
                                }}
                              />
                              <label
                                htmlFor={`type-${item.id}`}
                                className="flex items-center text-neutral-700 dark:text-neutral-300"
                              >
                                {getIconComponent(item.icon_name)?.({
                                  className: "text-2xl mr-2",
                                })}
                                {item.name}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 sm:p-6 flex-shrink-0 bg-neutral-50 dark:bg-neutral-900 dark:border-t dark:border-neutral-800 flex items-center justify-between">
                  <ButtonThird onClick={onClear} sizeClass="px-4 py-2 sm:px-5">
                    Clear
                  </ButtonThird>

                  <ButtonPrimary
                    onClick={() => onApply(onClose)}
                    sizeClass="px-4 py-2 sm:px-5"
                  >
                    Apply
                  </ButtonPrimary>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default MobileFiltersModal;